/**
 * B2M Web SDK - Simple event tracking for web applications
 * Only tracks custom events, no platform interceptors
 */
import Config from './core/config';
import Storage from './core/storage';
import UserID from './core/user-id';
import B2MCustomTracker from './trackers/b2m';
import BulkAPI from './api/bulk-api';
import CustomAPI from './api/custom-api';
import ExperimentAPI from './api/experiment-api';
import ExperimentManager from './core/experiments';
import SessionReplayLoader from './session-replay/session-replay-loader';
import GABridge from './integrations/ga-bridge';
import BrowserInfo from './utils/browser-info';
import { Logger } from './utils/logger';
import EventUtils from './utils/event-utils';
import PropertyDtypeRegistry from './utils/property-dtype-registry';
import { resolveCookieDomain } from './utils/cookie-utils';
import { loadChatWidget, loadChatWidgetFromConfig } from './chat-widget-loader';
import VisualRuntime from './core/visual/runtime';

// The SDK bundle's own URL, captured synchronously at load time —
// document.currentScript is null later inside async callbacks, and the chat
// widget URL is derived from this (same directory on the serving host).
const SDK_SCRIPT_SRC =
  (typeof document !== 'undefined' && document.currentScript && document.currentScript.src) || '';
// CSP nonce of the SDK tag (if any) — reused for the stylesheets and the
// editor bundle the visual runtime injects.
const SDK_SCRIPT_NONCE =
  (typeof document !== 'undefined' && document.currentScript &&
    (document.currentScript.nonce || document.currentScript.getAttribute('nonce'))) || '';

// Injected by webpack DefinePlugin from package.json (same value events carry
// as sdk_version); 'dev' when the source runs unbundled (unit tests).
const SDK_VERSION = (typeof __SDK_VERSION__ !== 'undefined') ? __SDK_VERSION__ : 'dev';

class B2MWebSDK {
  constructor() {
    // Readable at runtime (window.b2mWebSDK.version) — support debugging and
    // the version signature technology-detection tools (Wappalyzer) capture.
    this.version = SDK_VERSION;
    this.config = new Config();
    this.storage = new Storage();
    // Logger first: UserID logs its device-match decisions during construction.
    this.logger = new Logger();
    this.userId = new UserID(this.storage, this.logger);
    this.browserInfo = new BrowserInfo();
    this.eventUtils = new EventUtils();
    // Per-key dtype memory for undeclared event_params (ANALYTICS-6897).
    this.propertyDtypes = new PropertyDtypeRegistry(this.storage);
    this.bulkApi = new BulkAPI(this);
    this.customApi = new CustomAPI(this);
    this.experimentApi = new ExperimentAPI(this);
    this.experiments = new ExperimentManager(this);
    this.sessionReplay = new SessionReplayLoader(this);

    this.isInitialized = false;
    this.isOnline = navigator.onLine;
    this.retryCount = 0;
    
    // Initialize our dedicated B2M tracker (no interceptors)
    this.b2mTracker = new B2MCustomTracker(this);
    this.gaBridge = new GABridge(this);
  }
  
  async init(userConfig) {
    if (this.isInitialized) {
      this.logger.log("Web SDK already initialized");
      return;
    }
    
    // Configure the tracker
    this.config.init(userConfig);
    this.logger.setDebug(this.config.debug);

    // Identity source must be known before any resolution that can mint an id:
    // deviceClass mode never mints a salt and resolves the hash in init().
    this.userId.configure({
      identitySource: this.config.identitySource,
      identityResolveUrl: this.config.identityResolveUrl,
      identityResolveTimeoutMs: this.config.identityResolveTimeoutMs
    });

    // Cross-subdomain sharing for attribution (UTM/referrer) and the device
    // record. Resolves 'auto' (default) to the registrable domain so storage
    // mirrors those to a parent-domain cookie and sibling subdomains inherit
    // them. PERSON identity (the customer-supplied user id) and session state
    // are deliberately NOT shared: different apps can live on sibling
    // subdomains and must not leak logged-in identities into each other.
    // Resolves to null on localhost/IPs or when explicitly disabled.
    this.storage.cookieDomain = resolveCookieDomain(this.config.cookieDomain);
    if (this.storage.cookieDomain) {
      this.logger.log(`[B2M SDK] Cross-subdomain cookie domain: ${this.storage.cookieDomain}`);
    }

    // Remove identity/session cookies older SDK versions mirrored to the parent
    // domain — they leaked one subdomain's logged-in identity (e.g. a dashboard
    // login on analytics.<domain>) into every sibling app's tracking.
    this.storage.cleanupLegacyCrossDomainCookies();

    // Adopt the locally persisted user id BEFORE the early tracker fires its
    // first events, so they carry the correct user_id instead of a throwaway
    // temporary one.
    this.userId.adoptStoredId();

    // Mirror already-stored attribution to the parent-domain cookie on
    // every load — not only when a value is first written. Without this a
    // RETURNING user whose localStorage is populated but whose cookies are empty
    // (cookies expired, cleared, or written by an older SDK version that didn't
    // use cookies) would never get the cross-domain cookie repopulated.
    this.storage.reMirrorToCookies();
    
    // Validate configuration
    if (!this.config.validate()) {
      this.logger.error("Missing required configuration parameters");
      return;
    }
    
    // Set up network event listeners FIRST (before async operations)
    this._setupEventListeners();
    
    // Set up B2M tracker IMMEDIATELY with temporary ID
    // This ensures we capture pageview even before fingerprinting completes
    this._setupTrackerEarly();

    // GA bridge — install as early as possible. Anything Google Analytics has
    // already sent before this point is unrecoverable on the gtag/network
    // surfaces, so the SDK tag must sit above the GTM/gtag snippet.
    // No-op unless gaBridge.enabled is explicitly true.
    this.gaBridge.install(this.config.gaBridge);

    // If adoptStoredId() rejected the stored device record and minted a fresh
    // identity, declare the old→new bridge exactly once. This is the only
    // moment ANY party knows both ids — the backend stitches the two users
    // deterministically from this event, no server-side guessing needed.
    const identityReset = this.userId.consumePendingReset();
    if (identityReset) this._trackIdentityReset(identityReset);
    
    // Initialize user ID (async due to fingerprinting) - runs in background
    this.userId.init().then(() => {
      this.logger.log("User fingerprint ready:", this.userId.getId());

      // deviceClass mode may have migrated a pre-existing identity during
      // init() — the earlier consume ran before init() resolved, so declare
      // that bridge now (no-op when nothing was migrated).
      const lateReset = this.userId.consumePendingReset();
      if (lateReset) this._trackIdentityReset(lateReset);

      // Flush any events held while the user id was still a temp value — they
      // now resolve to the real fingerprint id at send time.
      this.sendEvents();

      // Auto-start session replay after user ID is ready so SR gets the correct ID
      if (this.config.enableSessionReplay) {
        this.sessionReplay.load().then(loaded => {
          if (loaded) this.sessionReplay.start();
        });
      }
    }).catch((e) => {
      // Fingerprint failed — don't strand the held events; release the hold and
      // flush with whatever id we have so data isn't lost.
      this.logger.error("User id init failed, flushing held events:", e);
      this.userId.idIsTemporary = false;
      this.sendEvents();
    });
    
    // Start automatic sending interval
    this._startSendingInterval();

    this.isInitialized = true;
    this.logger.log("B2M Web SDK initialized with configuration:", this.config.getAll());

    // Visual experiments: now that the merged config and the identity are in
    // place, match the URL, assign and apply (or finish editor/preview mode).
    // Runs for every init path (script tag, window.b2mConfig, manual b2mInit).
    if (this.visual) {
      try { this.visual.afterInit(); } catch (e) { this.logger.error('[B2M Visual] afterInit failed:', e); }
    }
    
    this._addMetaTag();

    // Log iframe and storage mode info
    if (this.config.iframeMode) {
      this.logger.log("Running in iframe mode - optimized settings applied");
      if (this.storage.isUsingMemoryFallback()) {
        this.logger.log("localStorage unavailable (3rd-party context) - using in-memory storage");
      }
    }
  }
  
  /**
   * Get the current device identity — stable per device+browser regardless of
   * who is logged in. It is a random per-install value validated against the
   * stored device-component vector on every visit, so two identical devices
   * never collapse into one user and signal drift never splits one user in two.
   */
  getUserFingerprint() {
    return this.userId.getFingerprintId();
  }

  /**
   * Deterministic hash of this device's class signals (model/OS/locale grade).
   * Identical devices produce identical values, so it is a stitching hint for
   * the backend — never an identity. Null until init() completes.
   */
  getDeviceClassHash() {
    return this.userId.getDeviceClassHash();
  }

  /**
   * Mark the page with <meta name="b2metric-sdk" content="<version>"> so
   * technology-detection tools (Wappalyzer, BuiltWith) can identify the SDK
   * and its version from the DOM. Added once; never throws into the host page.
   */
  _addMetaTag() {
    try {
      if (typeof document === 'undefined' || !document.head) return;
      if (document.querySelector('meta[name="b2metric-sdk"]')) return;
      const meta = document.createElement('meta');
      meta.name = 'b2metric-sdk';
      meta.content = this.version;
      document.head.appendChild(meta);
    } catch (e) {
      /* ignore — detection marker only */
    }
  }

  _setupEventListeners() {
    // Refresh the parent-domain cookies synchronously right before the page goes
    // away — this is the apex→subdomain redirect moment, so the session/
    // attribution cookies must be present for the subdomain to inherit them
    // (even if an external script reaped them mid-visit). document.cookie writes
    // are synchronous, so they land before the navigation completes.
    const refreshCookies = () => this.storage.refreshCookiesIfMissing();

    // Send events before page unload (fallback for older browsers)
    window.addEventListener("beforeunload", () => { refreshCookies(); this.sendEvents(true); });

    // pagehide is more reliable than beforeunload in iframe contexts
    // and on mobile browsers
    window.addEventListener("pagehide", () => { refreshCookies(); this.sendEvents(true); });

    // Send events when page becomes hidden (tab switch, minimize, etc.)
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.storage.refreshCookiesIfMissing();
        this.sendEvents(true);
      }
    });
    
    // Monitor online status
    window.addEventListener("online", () => {
      this.isOnline = true;
      this.logger.log("Device came online, attempting to send queued events");
      this.sendEvents();
    });
    
    window.addEventListener("offline", () => {
      this.isOnline = false;
      this.logger.log("Device went offline, events will be queued");
    });
  }
  
  _setupTracker() {
    if (this.config.respectDoNotTrack && navigator.doNotTrack === "1") {
      this.logger.log("Respecting Do Not Track - tracking disabled");
      return;
    }
    
    // Set up the B2M tracker for custom events
    this.b2mTracker.setup();
  }
  
  /**
   * Setup tracker early without waiting for fingerprint
   * This ensures we capture the first pageview immediately
   */
  _setupTrackerEarly() {
    // Skip DoNotTrack check for now - most users don't have it enabled
    // and we want to capture pageview immediately
    if (this.config.respectDoNotTrack && navigator.doNotTrack === "1") {
      this.logger.log("Respecting Do Not Track - tracking disabled");
      return;
    }
    
    // Set up the B2M tracker immediately
    this.b2mTracker.setup();
  }
  
  _startSendingInterval() {
    setInterval(() => this.sendEvents(), this.config.sendInterval);
  }

  /**
   * Declare an identity change (old→new bridge) exactly once. This event is
   * the only moment any party knows both ids — the backend stitches the two
   * users deterministically from it, no server-side guessing needed.
   */
  _trackIdentityReset(reset) {
    this.b2mTracker.track('identity_reset', {
      previous_user_id: reset.previousUserId,
      new_user_id: this.userId.getFingerprintId(),
      match_score: reset.matchScore != null
        ? Math.round(reset.matchScore * 100) / 100
        : undefined,
      changed_components: reset.changedComponents.join(',') || undefined
    });
  }
  
  trackEvent(eventName, eventData) {
    if (!this.isInitialized) {
      this.logger.error("Cannot track event - Web SDK not initialized");
      return;
    }

    // Use our B2M tracker for custom events
    window.b2m.track(eventName, eventData || {});
  }

  /**
   * Set the customer-supplied user identifier (e.g. internal user PK).
   * Call this once the user is logged in. Pass null to clear.
   * Cached experiment assignments are wiped because the identity changed.
   */
  setUserId(externalId) {
    // Logout arrives in many shapes from real integrations: null, undefined,
    // '' — and, through templating/serialization, the LITERAL strings
    // "undefined"/"null". All of them mean "clear"; without this, a string
    // "undefined" would become the stored identity of every logged-out user.
    const normalized = externalId == null ? '' : String(externalId).trim();
    const isClear = !normalized ||
      normalized.toLowerCase() === 'undefined' ||
      normalized.toLowerCase() === 'null';

    // Integrations commonly re-assert the current id on every page/route
    // change (b2mSetUserId(user?.id) in a layout effect). The identity did
    // not change, so the assignment cache must survive: wiping it here turned
    // every navigation into a fresh assign request — a duplicate-request
    // storm at the experiments service. The DL logout cleanup still runs
    // because a dataLayer-sourced login id can keep stamping events even
    // while externalId is already null.
    if ((isClear ? null : normalized) === this.userId.getExternalId()) {
      if (isClear && this.b2mTracker &&
          typeof this.b2mTracker.clearDataLayerUserId === 'function') {
        this.b2mTracker.clearDataLayerUserId();
      }
      this.logger.log('[B2M SDK] setUserId no-op — identity unchanged');
      return;
    }

    this.userId.setExternalId(isClear ? null : normalized);
    // Logout must also clear the dataLayer-sourced login id: in exclusive DL
    // modes it overrides the envelope user_id and would otherwise keep
    // stamping the previous user's identity until a full page reload.
    if (isClear && this.b2mTracker &&
        typeof this.b2mTracker.clearDataLayerUserId === 'function') {
      this.b2mTracker.clearDataLayerUserId();
    }
    // The assignment cache is NOT cleared on identity change: entries are
    // scoped per (experiment, identity), so the new identity simply misses
    // them and re-resolves while the previous identity's entries stay warm.
    // Clearing here made every refresh's null→login flip re-fire the whole
    // assign burst (observed live on webstage.e-bebek.com).
    this.sessionReplay.onUserChange();
    this.logger.log('[B2M SDK] External user ID set:', isClear ? null : normalized);
  }

  /**
   * Assign the current user to one or more experiments.
   * Returns the array of { ExperimentId, GroupId, GroupName } so the
   * caller can render the matching variant. Results are persisted in
   * localStorage so a returning user keeps the same group; the service is
   * re-asked once they are older than experimentCacheTtl (60 s), so platform
   * decisions reach users within that window.
   *
   * @param {string[]} experimentIds
   * @param {Object} [properties={}] - targeting attributes
   * @param {Object} [options]
   * @param {boolean} [options.forceRefresh=false]
   * @returns {Promise<Array>}
   */
  async assignExperiment(experimentIds, properties = {}, options = {}) {
    if (!this.isInitialized) {
      this.logger.error('Cannot assign experiments - Web SDK not initialized');
      return [];
    }
    return this.experiments.assign(experimentIds, properties, options);
  }

  /**
   * Synchronous lookup against the local cache. Returns the group name
   * if the user has already been assigned, otherwise null. Read it after
   * b2mAssignExperiment resolves — before that it can be a stale answer.
   */
  getExperimentGroup(experimentId) {
    return this.experiments.getGroup(experimentId);
  }

  /**
   * Synchronous lookup that returns the full assignment object
   * ({ ExperimentId, GroupId, GroupName }) or null.
   */
  getExperimentAssignment(experimentId) {
    return this.experiments.getAssignment(experimentId);
  }

  /**
   * Manually start session replay recording.
   * If the SR script is not yet loaded, loads it first then starts.
   */
  async startSessionReplay() {
    if (!this.isInitialized) {
      this.logger.error('[B2M SessionReplay] SDK not initialized');
      return;
    }
    const loaded = await this.sessionReplay.load();
    if (loaded) this.sessionReplay.start();
  }

  /** Stop session replay recording. */
  stopSessionReplay() {
    this.sessionReplay.stop();
  }

  /** Returns true if session replay is currently recording. */
  isSessionReplayActive() {
    return this.sessionReplay.isActive();
  }
  
  storeEvent(event) {
    // Enrich event with user ID and timestamp
    event._userId = this.userId.getId();
    // Captured at store-time: events queued before fingerprint resolves
    // carry the random temporary ID and need to be stitched server-side.
    event._idIsTemporary = this.userId.isIdTemporary();
    event._timestamp = new Date().toISOString();
    
    // Route to appropriate storage based on event source
    if (event.interceptor === "b2m") {
      // Store B2M events in bulk queue
      this.storage.addToBulkQueue(event);
      
      // Check if we should send events based on batch size
      if (this.storage.getBulkQueueSize() >= this.config.batchSize) {
        this.logger.log("Bulk event batch size threshold reached, sending events");
        this.sendBulkEvents();
      }
    } else if (event.platform === "custom") {
      // Store legacy custom events
      this.storage.addToCustomQueue(event);
      
      // Check if we should send events based on batch size
      if (this.storage.getCustomQueueSize() >= this.config.batchSize) {
        this.logger.log("Custom event batch size threshold reached, sending events");
        this.sendCustomEvents();
      }
    } else {
      // Store other events in bulk queue
      this.storage.addToBulkQueue(event);
      
      // Check if we should send events based on batch size
      if (this.storage.getBulkQueueSize() >= this.config.batchSize) {
        this.logger.log("Bulk event batch size threshold reached, sending events");
        this.sendBulkEvents();
      }
    }
  }
  
  storeEventList(events) {
    if (Array.isArray(events)) {
      events.forEach(event => this.storeEvent(event));
    } else {
      this.storeEvent(events);
    }
  }
  
  /**
   * Acquisition attribution (UTM + referrer) as USER properties, shared by both
   * senders so every event carries where the user came from. Returns raw
   * {key,value} pairs; callers map through their own createEventProperty.
   * Enforces a 32-day window: once it's been >32 days since the last campaign /
   * external referral, attribution stops being sent.
   * @returns {Array<{key:string,value:(string|number)}>}
   */
  getAttributionUserParams() {
    const out = [];
    const add = (k, v) => { if (v != null && v !== '' && !k.startsWith('_')) out.push({ key: k, value: v }); };

    // First-touch (initial_*) is the user's permanent acquisition record — it
    // answers "which channel won this user" in the central user table and is
    // deliberately NOT subject to the attribution window (Mixpanel/Amplitude
    // keep their initial_* user properties for life as well).
    const initialUtm = this.storage.getItem('b2m_initial_utm') || {};
    const initialReferrer = this.storage.getItem('b2m_initial_referrer') || {};
    Object.keys(initialUtm).forEach(k => add(k, initialUtm[k])); // initial_utm_*
    add('initial_referring_domain', initialReferrer.initial_referring_domain);

    // Last-touch (utm_*, referrer_domain) keeps the 32-day send window:
    // a source stops being credited for conversions after the window expires.
    const ts = this.storage.getItem('b2m_attribution_ts');
    if (ts && (Date.now() - ts) > 32 * 864e5) return out; // window expired
    const campaign = this.storage.getItem('b2m_campaign_params') || {};
    const referrer = this.storage.getItem('b2m_referrer') || {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id']
      .forEach(k => add(k, campaign[k]));
    add('referrer_domain', referrer.referrer_domain);
    return out;
  }

  /**
   * True until the real user_id is known. Used to hold events so nothing ships
   * with a throwaway temp id. The exception is page-unload (beacon) sends.
   */
  _holdForUserId(useBeacon) {
    if (useBeacon) return false;
    const hold = this.userId.isIdTemporary();
    if (hold) this.logger.log("[B2M SDK] Holding events until user id is ready");
    return hold;
  }

  /**
   * Consent-aware capture: when requireConsent is on and consent hasn't been
   * given, NOTHING is sent — not even on page unload (no consent = no transmit).
   * Events stay captured in the queue and are flushed by giveConsent().
   */
  _holdForConsent() {
    if (this.config.requireConsent && !this.b2mTracker.hasConsent()) {
      this.logger.log("[B2M SDK] Holding events until consent is given");
      return true;
    }
    return false;
  }

  /**
   * Called once consent is granted. A consent platform may have blocked
   * document.cookie before consent — so the cookieDomain probe could have
   * failed (→ null) and attribution/identity cookies may never have been
   * written. Re-resolve the domain and re-mirror the values to cookies now,
   * so cross-subdomain inheritance works for users who consent on landing.
   */
  persistConsentedState() {
    this.storage.cookieDomain = resolveCookieDomain(this.config.cookieDomain);
    this.storage.reMirrorToCookies();
    if (this.storage.cookieDomain) {
      this.logger.log(`[B2M SDK] Re-persisted cookies after consent on ${this.storage.cookieDomain}`);
    }
  }

  sendEvents(useBeacon = false) {
    if (!this.isInitialized) {
      this.logger.error("Cannot send events - Web SDK not initialized");
      return;
    }

    // Send both types of events
    this.sendBulkEvents(useBeacon);
    this.sendCustomEvents(useBeacon);
  }

  sendBulkEvents(useBeacon = false) {
    if (this._holdForConsent()) return;          // consent gate (even on unload)
    if (this._holdForUserId(useBeacon)) return;  // user-id gate (except unload)
    this.storage.refreshCookiesIfMissing();      // repopulate cookies if reaped
    this.bulkApi.sendEvents(useBeacon);
  }

  sendCustomEvents(useBeacon = false) {
    if (this._holdForConsent()) return;
    if (this._holdForUserId(useBeacon)) return;
    this.storage.refreshCookiesIfMissing();
    this.customApi.sendEvents(useBeacon);
  }
}

// Create singleton instance
const webSDK = new B2MWebSDK();

// Visual experiments runtime — booted at script evaluation (NOT after
// DOMContentLoaded) so matched elements can be hidden before first paint and
// editor/preview URL params are handled before anything else runs.
const visualRuntime = new VisualRuntime(webSDK, { scriptSrc: SDK_SCRIPT_SRC, nonce: SDK_SCRIPT_NONCE });
webSDK.visual = visualRuntime;
try {
  visualRuntime.earlyBoot();
} catch (e) {
  console.error('[B2M SDK] visual early boot failed:', e);
}

// Expose global functions (async for init due to fingerprinting)
window.b2mInit = async (config) => await webSDK.init(config);
window.b2mTrack = (eventName, eventData) => webSDK.trackEvent(eventName, eventData);
window.b2mSendEvents = () => webSDK.sendEvents();

// A/B test public API
window.b2mSetUserId = (externalId) => webSDK.setUserId(externalId);
window.b2mAssignExperiment = (ids, properties, options) =>
  webSDK.assignExperiment(ids, properties, options);
window.b2mGetExperimentGroup = (experimentId) => webSDK.getExperimentGroup(experimentId);
window.b2mGetExperimentAssignment = (experimentId) => webSDK.getExperimentAssignment(experimentId);

// Visual experiments debug API
window.b2mVisual = {
  status: () => visualRuntime.status(),
  reapply: () => visualRuntime.reapply(),
  revert: () => visualRuntime.revert(),
  preview: (experimentId, groupId) => visualRuntime.preview(experimentId, groupId),
  get ready() { return visualRuntime.ready; }
};

// Session Replay public API
window.b2mStartSessionReplay = () => webSDK.startSessionReplay();
window.b2mStopSessionReplay = () => webSDK.stopSessionReplay();
window.b2mIsSessionReplayActive = () => webSDK.isSessionReplayActive();

// Session ID getter — SR SDK calls this before each batch to get the current session ID
window.b2mGetSessionId = () => webSDK.storage.getSessionId();

// Device identity getters (debugging / server-side stitching)
window.b2mGetDeviceId = () => webSDK.getUserFingerprint();
window.b2mGetDeviceClassHash = () => webSDK.getDeviceClassHash();

// Expose SDK instance globally for debugging and access
window.b2mWebSDK = webSDK;

// Brand-level global, a stable signature for technology-detection tools.
// window.b2metric is also the Session Replay script's public API, which
// replaces the whole object when it loads — so only claim the name while it
// is free and never overwrite an existing value.
if (!window.b2metric) {
  window.b2metric = {
    version: webSDK.version,
    init: window.b2mInit,
    track: window.b2mTrack
  };
}

// Auto-initialization from script tag data attributes
window.b2mReady = (async function autoInit() {
  try {
  webSDK.logger.log('[B2M SDK] Auto-initialization started...');
  
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    webSDK.logger.log('[B2M SDK] Waiting for DOM...');
    await new Promise(resolve => {
      document.addEventListener('DOMContentLoaded', resolve, { once: true });
    });
  }
  
  webSDK.logger.log('[B2M SDK] DOM ready, looking for script tag...');
  
  // Find the script tag with data-api-key
  const scriptTag = document.querySelector('script[data-api-key]');
  
  if (scriptTag && scriptTag.hasAttribute('data-api-key')) {
    webSDK.logger.log('[B2M SDK] Found script tag with data-api-key');
    
    const config = {
      apiKey: scriptTag.getAttribute('data-api-key'),
      debug: scriptTag.getAttribute('data-debug') === 'true',
      batchSize: parseInt(scriptTag.getAttribute('data-batch-size')) || 5,
      sendInterval: parseInt(scriptTag.getAttribute('data-send-interval')) || 30000,
      respectDoNotTrack: scriptTag.getAttribute('data-respect-dnt') !== 'false',
      requireConsent: scriptTag.getAttribute('data-require-consent') === 'true',
      enableSPATracking: scriptTag.getAttribute('data-spa-tracking') !== 'false',
      sessionTimeout: parseInt(scriptTag.getAttribute('data-session-timeout')) || 1800000,
      duplicatePreventionTimeout: parseInt(scriptTag.getAttribute('data-duplicate-timeout')) || 5000,
      maxRetries: parseInt(scriptTag.getAttribute('data-max-retries')) || 3,
      retryTimeout: parseInt(scriptTag.getAttribute('data-retry-timeout')) || 30000,
      enableSessionReplay: scriptTag.getAttribute('data-session-replay') === 'true',
      // script/host fall back to baked-in defaults in Config when attribute is absent
      ...(scriptTag.hasAttribute('data-session-replay-script') && {
        sessionReplayScriptUrl: scriptTag.getAttribute('data-session-replay-script')
      }),
      ...(scriptTag.hasAttribute('data-session-replay-host') && {
        sessionReplayHost: scriptTag.getAttribute('data-session-replay-host')
      }),
      sessionReplaySiteId: scriptTag.getAttribute('data-session-replay-site-id')
        ? parseInt(scriptTag.getAttribute('data-session-replay-site-id'))
        : null,
      sessionReplayUseCustomSessionId:
        scriptTag.getAttribute('data-session-replay-use-custom-session-id') === 'true'
    };

    // Legacy data-enable-gtm-listener / data-gtm-exclude-events are intentionally
    // not read. If still present on the script tag they are harmless no-ops.

    // Tracking mode: native | datalayer | hybrid (invalid/missing → native)
    const trackingModeAttr = scriptTag.getAttribute('data-tracking-mode');
    if (trackingModeAttr && trackingModeAttr.trim()) {
      config.trackingMode = trackingModeAttr.trim().toLowerCase();
      webSDK.logger.log(`[B2M SDK] trackingMode from attribute: ${config.trackingMode}`);
    }
    
    // GA bridge — data-ga-bridge="true" enables it; mode/endpoints are optional.
    // data-ga-bridge-endpoints is a comma-separated list of extra collect URLs,
    // needed for server-side GTM (e.g. "sgtm.example.com/g/collect").
    if (scriptTag.getAttribute('data-ga-bridge') === 'true') {
      const gaBridgeMode = scriptTag.getAttribute('data-ga-bridge-mode');
      const gaBridgeEndpoints = scriptTag.getAttribute('data-ga-bridge-endpoints');
      config.gaBridge = {
        enabled: true,
        mode: gaBridgeMode && gaBridgeMode.trim() ? gaBridgeMode.trim().toLowerCase() : 'auto',
        endpoints: gaBridgeEndpoints
          ? gaBridgeEndpoints.split(',').map((e) => e.trim()).filter(Boolean)
          : [],
        // data-ga-bridge-exclusive="true": only GA-mirrored events, no native auto-tracking
        exclusive: scriptTag.getAttribute('data-ga-bridge-exclusive') === 'true'
      };
      webSDK.logger.log(`[B2M SDK] GA bridge enabled (mode: ${config.gaBridge.mode})`);
    }

    // Parse custom event library (e.g. data-custom-event-lib="pulpo")
    const customEventLib = scriptTag.getAttribute('data-custom-event-lib');
    if (customEventLib) {
      config.customEventLib = customEventLib.trim().toLowerCase();
      webSDK.logger.log(`[B2M SDK] Custom event library: ${config.customEventLib}`);
    }

    // Parse optional app identifier (free-form name). Set only when non-empty —
    // empty / missing keeps hostname auto-detect (backward compatible).
    // e.g. data-app-identifier="checkout-app"
    const appIdentifier = scriptTag.getAttribute('data-app-identifier');
    if (appIdentifier && appIdentifier.trim()) {
      config.appIdentifier = appIdentifier.trim();
      webSDK.logger.log(`[B2M SDK] App identifier (manual): ${config.appIdentifier}`);
    }

    // Parse optional cookie domain for cross-subdomain attribution/session sharing
    // (e.g. data-cookie-domain=".birfatura.com").
    const cookieDomain = scriptTag.getAttribute('data-cookie-domain');
    if (cookieDomain && cookieDomain.trim()) {
      config.cookieDomain = cookieDomain.trim();
      webSDK.logger.log(`[B2M SDK] Cookie domain (cross-subdomain): ${config.cookieDomain}`);
    }

    // A/B-only mode — keeps ab_test_view + user props, drops behavioural events.
    // data-ab-mode="true" → dedup behavioural data with BigQuery.
    if (scriptTag.getAttribute('data-ab-mode') === 'true') {
      config.abMode = true;
      webSDK.logger.log('[B2M SDK] abMode enabled via data attribute');
    }

    // Visual experiments: data-visual-experiments="false" is the kill switch;
    // data-visual-experiment-ids lists the experiments to read from
    // experiments-api; data-visual-config-url / data-visual-editor-origins
    // override defaults.
    const visualAttr = scriptTag.getAttribute('data-visual-experiments');
    if (visualAttr && visualAttr.trim().toLowerCase() === 'false') {
      config.visualExperiments = false;
      webSDK.logger.log('[B2M SDK] visual experiments disabled via data attribute');
    }
    const visualExperimentIds = scriptTag.getAttribute('data-visual-experiment-ids');
    if (visualExperimentIds && visualExperimentIds.trim()) {
      config.visualExperimentIds = visualExperimentIds.split(/[\s,]+/).filter(Boolean);
    }
    const visualConfigUrl = scriptTag.getAttribute('data-visual-config-url');
    if (visualConfigUrl && visualConfigUrl.trim()) config.visualConfigUrl = visualConfigUrl.trim();
    const visualEditorOrigins = scriptTag.getAttribute('data-visual-editor-origins');
    if (visualEditorOrigins && visualEditorOrigins.trim()) {
      config.visualEditorOrigins = visualEditorOrigins.split(',').map((o) => o.trim()).filter(Boolean);
    }
    const visualEditorUrl = scriptTag.getAttribute('data-visual-editor-url');
    if (visualEditorUrl && visualEditorUrl.trim()) config.visualEditorUrl = visualEditorUrl.trim();

    // Property type schema for dtype consistency
    // (e.g. data-property-types='{"purchase_count":"number","product_id":"string"}')
    const propTypes = scriptTag.getAttribute('data-property-types');
    if (propTypes) {
      try { config.propertyTypes = JSON.parse(propTypes); }
      catch (e) { console.error('[B2M SDK] data-property-types JSON parse error:', e); }
    }

    // Automatic dtype stabilization for undeclared keys is on by default;
    // data-auto-property-types="false" restores raw pass-through.
    const autoPropTypes = scriptTag.getAttribute('data-auto-property-types');
    if (autoPropTypes && autoPropTypes.trim().toLowerCase() === 'false') {
      config.autoPropertyTypes = false;
      webSDK.logger.log('[B2M SDK] autoPropertyTypes disabled via data attribute');
    }

    webSDK.logger.log('[B2M SDK] Config:', config);
    
    // Parse event groups
    const eventGroupsAttr = scriptTag.getAttribute('data-event-groups');
    if (eventGroupsAttr) {
      config.eventGroups = {};
      eventGroupsAttr.split(',').forEach(group => {
        config.eventGroups[group.trim()] = { enabled: true };
      });
      webSDK.logger.log('[B2M SDK] Event groups:', config.eventGroups);
    }
    
    // Parse custom events
    const customEventsAttr = scriptTag.getAttribute('data-custom-events');
    let customEvents = null;
    if (customEventsAttr) {
      try {
        customEvents = JSON.parse(customEventsAttr);
        webSDK.logger.log('[B2M SDK] Custom events:', customEvents);
      } catch (e) {
        console.error('[B2M SDK] Error parsing custom events', e);
      }
    }
    
    // Auto-initialize SDK
    webSDK.logger.log('[B2M SDK] Initializing SDK...');
    await window.b2mInit(config);
    webSDK.logger.log('[B2M SDK] SDK initialized successfully!');

    // Chat widget module: when the SDK tag carries a chat identity
    // (data-chat-integration-id / data-chat-tenant), load the widget bundle —
    // the customer keeps a single script tag.
    loadChatWidget(scriptTag, webSDK.logger);
    
    // Set up custom events if provided
    if (customEvents && Array.isArray(customEvents)) {
      customEvents.forEach(event => {
        const eventData = event.d || {};
        
        if (event.t === 'onload') {
          // Track on page load (immediately)
          window.b2mTrack(event.n, eventData);
        } else if (event.t === 'button' && event.s) {
          // Track on button click
          const element = document.querySelector(event.s);
          if (element) {
            element.addEventListener('click', () => {
              window.b2mTrack(event.n, eventData);
            });
          }
        } else if (event.t === 'form' && event.s) {
          // Track on form submit
          const form = document.querySelector(event.s);
          if (form) {
            form.addEventListener('submit', () => {
              window.b2mTrack(event.n, eventData);
            });
          }
        }
      });
    }
    
    return true;
  } else if (window.b2mConfig && window.b2mConfig.apiKey) {
    // GTM tag-template / snippet installs: no attributed script tag — the
    // page (or the sandboxed GTM template) sets window.b2mConfig BEFORE
    // loading the SDK. Chat fields ride along under chat* keys.
    const g = window.b2mConfig;
    webSDK.logger.log('[B2M SDK] Initializing from window.b2mConfig...');
    await window.b2mInit(g);

    loadChatWidgetFromConfig(
      {
        ...(g.chatTenant && { 'data-tenant': g.chatTenant }),
        ...(g.chatIntegrationId && { 'data-integration-id': g.chatIntegrationId }),
        ...(g.chatApiKey && { 'data-chat-api-key': g.chatApiKey }),
        ...(g.chatWidgetUrl && { 'data-widget-url': g.chatWidgetUrl }),
        ...(g.chatConfigUrl && { 'data-config-url': g.chatConfigUrl }),
        ...(g.chatApiUrl && { 'data-api-url': g.chatApiUrl }),
        ...(g.chatPosition && { 'data-position': g.chatPosition }),
      },
      SDK_SCRIPT_SRC,
      webSDK.logger
    );
    return true;
  } else {
    webSDK.logger.log('[B2M SDK] No script tag found with data-api-key, waiting for manual init');
    return false;
  }
  } catch (e) {
    console.error('[B2M SDK] Auto-initialization failed:', e);
    return false;
  }
})();

export default webSDK; 